import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  Typography,
  Alert,
  Link,
  CircularProgress
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import api from '../../utils/axios';

interface RSSFeed {
  id: string;
  title: string;
  url: string;
  category: string;
}

interface RSSItem {
  id: string;
  title: string;
  link: string;
  description: string;
  pubDate: string;
  feedId: string;
}

interface RSSFeedsProps {
  isWidget?: boolean;
}

const RSSFeeds: React.FC<RSSFeedsProps> = ({ isWidget = false }) => {
  const [selectedFeed, setSelectedFeed] = useState<string | null>(null);

  const { data: feedsData, isLoading: feedsLoading, error: feedsError } = useQuery<{ success: boolean; data: RSSFeed[] }>({
    queryKey: ['rss-feeds'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: RSSFeed[] }>('/news/rss-feeds');
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const { data: itemsData, isLoading: itemsLoading, error: itemsError } = useQuery<{ success: boolean; data: RSSItem[] }>({
    queryKey: ['rss-items', selectedFeed],
    queryFn: async () => {
      if (selectedFeed) {
        const response = await api.get<{ success: boolean; data: RSSItem[] }>(`/news/rss-items/${selectedFeed}`);
        return response.data;
      } else {
        // For widget mode, get all items
        const response = await api.get<{ success: boolean; data: RSSItem[] }>('/news/rss-items');
        return response.data;
      }
    },
    enabled: !!selectedFeed || isWidget,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const feeds = feedsData?.data || [];
  const items = itemsData?.data || [];

  if (feedsError || itemsError) {
    return (
      <Alert severity="error">
        Failed to load news feeds. Please try again later.
      </Alert>
    );
  }

  if (isWidget) {
    return (
      <Box sx={{ height: '100%', overflow: 'auto' }}>
        {itemsLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : items.length > 0 ? (
          <List>
            {items.map((item: RSSItem) => (
              <ListItem key={item.id} divider>
                <Box>
                  <Link
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    color="primary"
                    underline="hover"
                  >
                    <Typography variant="h6" gutterBottom>
                      {item.title}
                    </Typography>
                  </Link>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {item.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(item.pubDate).toLocaleString()}
                  </Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        ) : (
          <Box textAlign="center" p={3}>
            <Typography color="text.secondary">
              No news items available at the moment.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 2fr' }, gap: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Available Feeds
          </Typography>
          {feedsLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : feeds.length > 0 ? (
            <List>
              {feeds.map((feed: RSSFeed) => (
                <ListItem
                  key={feed.id}
                  button
                  selected={selectedFeed === feed.id}
                  onClick={() => setSelectedFeed(feed.id)}
                  sx={{
                    borderRadius: 1,
                    mb: 1,
                    '&.Mui-selected': {
                      bgcolor: 'primary.light',
                      '&:hover': {
                        bgcolor: 'primary.light',
                      },
                    },
                  }}
                >
                  <Box>
                    <Typography variant="subtitle1">{feed.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feed.category}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography color="text.secondary">
                No RSS feeds available.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {feeds.find((f: RSSFeed) => f.id === selectedFeed)?.title || 'Select a Feed'}
          </Typography>
          {selectedFeed ? (
            itemsLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : items.length > 0 ? (
              <List>
                {items.map((item: RSSItem) => (
                  <ListItem key={item.id} divider>
                    <Box>
                      <Link
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="primary"
                        underline="hover"
                      >
                        <Typography variant="h6" gutterBottom>
                          {item.title}
                        </Typography>
                      </Link>
                      <Typography variant="body2" paragraph>
                        {item.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Published: {new Date(item.pubDate).toLocaleString()}
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box textAlign="center" p={3}>
                <Typography color="text.secondary">
                  No items found for this feed.
                </Typography>
              </Box>
            )
          ) : (
            <Box textAlign="center" color="text.secondary" py={4}>
              Select a feed from the list to view its items
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RSSFeeds; 